import React, { useMemo, useState, useEffect } from 'react';
import forge from 'apis/forge';
import { GetSiteDesignSystemInfoResponse } from 'dragonmouse-shared/api/site';
import { Frame } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useDispatch } from 'react-redux';
import { canExportSvg } from 'redux/db/util';
import FrameDetails from '../FrameDetails';
import * as Base from '../styles';
import * as S from './styles';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { showContextMenu } from 'redux/mode/actions';
import { useTheme } from 'styled-components';
import DropdownMenu from 'components/Widget/DropdownMenu';

interface FramePropertiesProps {
	frame: Frame | undefined;
}
const FrameProperties = ({ frame }: FramePropertiesProps) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const tooltipId = 'header-tooltip';
	const [dsInfo, setDsInfo] = useState<GetSiteDesignSystemInfoResponse | null>(null);

	// Fetch DS info when frame has a siteId
	useEffect(() => {
		if (!frame?.siteId) {
			setDsInfo(null);
			return;
		}

		let cancelled = false;

		const fetchDsInfo = async () => {
			try {
				const data = await forge.getSiteDesignSystemInfo({ siteId: frame.siteId });
				if (!cancelled) {
					setDsInfo(data);
				}
			} catch (error) {
				console.error('Failed to fetch site DS info:', error);
				if (!cancelled) {
					setDsInfo(null);
				}
			}
		};

		fetchDsInfo();

		return () => {
			cancelled = true;
		};
	}, [frame?.siteId]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onContextMenu = async (e: React.MouseEvent) => {
		if (!frame) return;
		const menuItems = [MenuItemAction.createRenameFrame(frame)];
		if (e.target instanceof HTMLElement) {
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const onModeSelect = (modeId: string) => {
		if (!frame) return;
		const isDeselecting = frame.metadata?.mode === modeId;
		forge.updateFrame({
			update: {
				id: frame.id,
				metadata: {
					...frame.metadata,
					mode: isDeselecting ? null : modeId,
				},
			},
		});
	};

	//------------------------------------------------------------------------------------------------
	// MENU ITEMS
	//------------------------------------------------------------------------------------------------
	const copyMenuItems = useMemo(() => {
		if (!frame) return [];

		const items = [
			MenuItemAction.createCopyFrameImage(frame),
			...(canExportSvg([frame]) ? [MenuItemAction.createCopyFrameSvg(frame)] : []),
			MenuItemAction.createCopyFrameHtml(frame),
		];

		return items;
	}, [frame]);

	const downloadMenuItems = useMemo(() => {
		if (!frame) return [];
		return [
			MenuItemAction.createDownloadFrame(frame, 'png'),
			MenuItemAction.createDownloadFrame(frame, 'svg'),
			MenuItemAction.createDownloadFrame(frame, 'html'),
		];
	}, [frame]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderDesignSystemCard = () => {
		if (!dsInfo?.designSystemName) return null;
		const modes = dsInfo.dsModes;
		const hasModes = modes && modes.length >= 2;

		// Reconcile active mode
		const defaultModeId = modes?.find((t) => t.default)?.id ?? modes?.[0]?.id;
		const storedMode = frame?.metadata?.mode;
		const currentMode =
			storedMode && modes?.some((t) => t.id === storedMode) ? storedMode : defaultModeId;

		return (
			<S.DsCard>
				<S.DsHeader>
					<S.DsIcon>
						<Icon type={IconType.STACK} width={14} height={14} />
					</S.DsIcon>
					<S.DsName>{dsInfo.designSystemName}</S.DsName>
				</S.DsHeader>
				{hasModes && (
					<S.DsModeSection>
						{modes.map((t) => {
							const isActive = currentMode === t.id;
							return (
								<S.DsModeOption key={t.id} $active={isActive} onClick={() => onModeSelect(t.id)}>
									<S.DsModeOptionSwatch $color={t.bg ?? '#f8f8f8'} />
									{t.label}
								</S.DsModeOption>
							);
						})}
					</S.DsModeSection>
				)}
			</S.DsCard>
		);
	};

	if (!frame) return;
	const headerLabel = frame.title ?? 'Untitled Design';

	return (
		<Base.Base>
			<Base.Divider />
			<S.HeaderBase>
				<S.HeaderLabel onContextMenu={(e) => onContextMenu(e)}>{headerLabel}</S.HeaderLabel>
				<S.HeaderActionsSection>
					<DropdownMenu menuItems={copyMenuItems}>
						<S.DropdownTrigger data-tooltip-id={tooltipId} data-tooltip-content="Copy">
							<Icon type={IconType.COPY} width={16} height={16} color={theme.gray[1000]} />
						</S.DropdownTrigger>
					</DropdownMenu>
					<DropdownMenu menuItems={downloadMenuItems}>
						<S.DropdownTrigger data-tooltip-id={tooltipId} data-tooltip-content="Download">
							<Icon
								type={IconType.LINE_ARROW_DOWN}
								width={16}
								height={16}
								color={theme.gray[1000]}
							/>
						</S.DropdownTrigger>
					</DropdownMenu>
					<Tooltip id={tooltipId} placement="top" />
				</S.HeaderActionsSection>
			</S.HeaderBase>
			<Base.ScrollingContainer>
				<S.Dimensions>
					{frame.width} x {frame.height}
				</S.Dimensions>
				{renderDesignSystemCard()}
				<FrameDetails frame={frame} />
			</Base.ScrollingContainer>
		</Base.Base>
	);
};

export default FrameProperties;
